import { Alert, Box, Button, Divider, IconButton, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { TransitionGroup } from "react-transition-group";
import LeftArrow from "src/components/shared/icon/LeftArrow";
import useAuthGlobal from "src/context/useAuthGlobal";
import convertCurrency, { convertIntMoney } from "src/helper/convertCurrency";
import useRedirect from "src/helper/direct";
import useOrderCreate from "src/hooks/orders/useOrderCreate";
import useInventoryList from "src/hooks/inventory/useInventoryList";
import useCustomerList from "src/hooks/user/customer/useCustomerList";
import useCustomerBrandDetail from "src/hooks/user/customer/useCustomerBrandDetail";
import useDebounce from "src/hooks/useDebounce";
import ChangeAddressDialog from "src/pages/orders/all-order/ChangeAddressDialog";
import ProductForm from "./ProductForm";
import { Controller, FormProvider, useFieldArray, useForm } from "react-hook-form";
import AddressList from "./AddressList";
import FormControlAutocomplete from "src/components/shared/FormControlAutocomplete";
import FormControlInput from "src/components/shared/FormControlInput";
import DeliveryInfo from "./DeliveryInfo";
import { enqueueSnackbar } from "notistack";
import { orderTrackingType } from "src/constant";
import { orderStatusFormatted } from "src/constant";
import { yupResolver } from "@hookform/resolvers/yup";
import { getAddressPath } from "src/utils/address";
import { PERMISSIONS, useRoleCheck } from "src/hooks/permission/permission";
import useOrderSchemaCreate from "../utils/useOrderSchemaCreate";
import SearchList from "src/components/shared/SearchList";
import { Add } from "@mui/icons-material";
import ListCategories from "src/components/shared/ListCategories";
import useWareHouseList from "src/hooks/warehouse/useWareHouseList";

export default function CreateOrder() {
  const redirect = useRedirect();
  const permissionCheck = useRoleCheck();
  const { userMe } = useAuthGlobal();
  const { OrderDefault, OrderSchema } = useOrderSchemaCreate({ userMe });
  const form = useForm({
    defaultValues: OrderDefault,
    resolver: yupResolver(OrderSchema),
  });

  const {
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const isAdminRole = permissionCheck([PERMISSIONS.VIEW_WAREHOUSE_ALL]);

  const productsWatch = watch("products");
  const shippingFeeWatch = watch("shippingFee");
  const warehouseWatch = watch("warehouse");
  const customerWatch = watch("customer");

  const [isChangedAddress, setIsChangedAddress] = useState(false);
  const [productSearchInput, setProductSearchInput] = useState("");
  const [selectedWarehouse, setSelectedWarehouse] = useState({ skip: 0, limit: 20 });
  const { data: warehouseCategories } = useWareHouseList(selectedWarehouse);

  const productsField = useFieldArray({
    control: control,
    name: "products",
  });

  const { data: customers } = useCustomerList({ limit: 200, skip: 0 });

  const { data: inventory, isLoading: inventoryLoading } = useInventoryList(
    {
      limit: 200,
      skip: 0,
      warehouse: warehouseWatch?._id,
      productGroupType: "Retail",
    },
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      keepPreviousData: true,
    },
  );

  const { data: customerBrandRes } = useCustomerBrandDetail(customerWatch?.value, undefined, {
    revalidateOnFocus: false,
    revalidateIfStale: false,
  });

  const inventoryOptions = useMemo(
    () =>
      inventory?.data?.map((v) => ({
        label: v?.product?.name,
        value: v?._id,
        brand: v?.product?.brand,
      })) ?? [],
    [inventory?.data],
  );

  const customerBrandList = customerBrandRes?.data;

  const { trigger: createOrder } = useOrderCreate();

  const onSubmit = async (values) => {
    const res = await createOrder({
      body: {
        type: orderTrackingType.new,
        status: orderStatusFormatted.WaitToExport,
        shippingFee: Number(values?.shippingFee),
        cod: values?.hasCOD === "COD" ? values?.cod : 0,
        pickUpAddress: [values?.warehouse?.detailAddress, values?.warehouse?.ward?.path].filter(Boolean).join(", "),
        note: values?.note,
        trackingStatus: orderStatusFormatted.WaitToExport,
        pickUpType: values?.pickUpType,
        pickupDate: values?.pickupDate,
        warehouse: warehouseWatch?._id ?? values?.warehouse?.value,
        customer: values?.customer?.value,
        sales: values?.customer?.sales?._id,
        paymentMethod: values?.paymentMethod?.value,
        shippingProvider: values?.shippingProvider?.value,
        recipient: {
          detailAddress: [values?.recipient?.detailAddress, getAddressPath(values?.recipient?.ward)].filter(Boolean).join(", "),
          name: values?.customer?.fullName,
          phone: values?.recipient?.phone,
          ward: values?.recipient?.ward?._id,
        },
        orderDetails: values?.products?.map((v) => ({
          qty: v?.qty,
          discount: Number(v?.discount),
          productModel: v?.productModel?.value,
        })),
      },
    });
    if (res?.success) {
      redirect("/orders?tab=all");
      enqueueSnackbar("Thành công", { variant: "success" });
      return;
    }
    return enqueueSnackbar(res?.error?.message ?? "Xử lý yêu cầu thất bại", { variant: "error" });
  };

  const handleChangeAddress = () => {
    setIsChangedAddress((prev) => !prev);
  };

  const handleRedirect = () => {
    redirect("/orders?tab=all");
  };

  const handleAddProduct = () => {
    productsField.append({ productModel: null, product: null, qty: 1, discount: 0 });
  };

  const handleRemoveProduct = (index) => {
    productsField.remove(index);
  };

  const isQtyLargerThanStock = useMemo(() => {
    return (
      Array.isArray({}?.orderDetails) &&
      {}?.orderDetails?.some((item) => {
        const orderQty = item?.qty ?? 0;
        const stockQty = item?.productModel?.qty ?? 0;

        return orderQty > stockQty;
      })
    );
  }, []);

  const totalCOD = useMemo(() => {
    const result = productsWatch.reduce((acc, curr) => {
      const price = curr?.productModel?.price || 0;
      const qty = curr?.qty || 0;
      const discount = Number(curr?.discount) || 0;
      const total = price * qty * (1 - discount / 100);
      return acc + total;
    }, 0);

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsWatch, shippingFeeWatch]);

  const customerOptions = useMemo(() => {
    return customers?.data?.map((v) => ({
      ...v,
      label: v?.fullName,
      value: v?._id,
    }));
  }, [customers]);

  useEffect(() => {
    const defaultCod = productsWatch.reduce((acc, curr) => {
      const price = curr?.productModel?.price || 0;
      const qty = curr?.qty || 0;
      const discount = Number(curr?.discount) || 0;
      const total = price * qty * (1 - discount / 100) + shippingFeeWatch;
      return acc + total;
    }, 0);

    setValue("cod", Math.floor(defaultCod / 1000) * 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsWatch, shippingFeeWatch]);

  useEffect(() => {
    if (userMe?.currentWarehouse) {
      setValue("warehouse", userMe.currentWarehouse);
    }
  }, [userMe?.currentWarehouse, setValue]);

  return (
    <FormProvider {...form}>
      <Typography
        fontSize="18px"
        fontWeight="bold"
      >
        Quản lý đơn hàng
      </Typography>
      <Typography>Đơn hàng</Typography>
      {!isChangedAddress ? (
        <Stack sx={{ p: "12px", border: "1px solid var(--grey)", borderRadius: "12px", mt: "12px" }}>
          <Box
            display="flex"
            alignItems="center"
            gap="6px"
          >
            <IconButton
              onClick={handleRedirect}
              sx={{
                width: "28px",
                height: "28px",
                background: "var(--black) !important",
              }}
            >
              <LeftArrow color="var(--white)" />
            </IconButton>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
            >
              Quay lại Danh sách đơn hàng
            </Typography>
          </Box>
          <Typography
            fontWeight="bold"
            fontSize="18px"
          >
            Tạo đơn hàng
          </Typography>
          <Divider sx={{ borderColor: "var(--grey) !important", my: "12px" }} />
          <FormControlAutocomplete
            name="customer"
            label="Khách hàng"
            options={customerOptions}
            placeholder="Khách hàng"
            required
          />
          {customerWatch?.totalDebt > customerWatch?.debtLimit ? (
            <Alert
              severity="error"
              sx={{ mt: 1.5 }}
              color="error"
            >
              KH đã <strong>vượt hạn mức</strong> công nợ!
            </Alert>
          ) : customerWatch?.totalDebt >= customerWatch?.debtLimit * 0.9 ? (
            <Alert
              severity="warning"
              sx={{ mt: 1.5 }}
              color="warning"
            >
              KH đã sử dụng <strong>trên 90%</strong> hạn mức công nợ!
            </Alert>
          ) : null}
          {customerWatch?.sales?.fullName && (
            <Stack
              direction="row"
              gap="12px"
              mt="12px"
            >
              <Box
                sx={{
                  background: "var(--light-grey)",
                  width: "50%",
                  borderRadius: "6px",
                  p: "12px",
                }}
              >
                <Typography fontWeight="bold">NV phụ trách</Typography>
                <Typography variant="body2">Tên: {customerWatch?.sales?.fullName}</Typography>
                <Typography variant="body2">Email: {customerWatch?.sales?.email ?? "N/A"}</Typography>
                <Typography variant="body2">SDT: {customerWatch?.sales?.phone ?? "N/A"}</Typography>
              </Box>
              {/* ADDRESS */}
              <AddressList onChangeAddressMode={handleChangeAddress} />
            </Stack>
          )}
          {isAdminRole && (
            <Box mt={2}>
              <Controller
                control={control}
                name="warehouse"
                render={({ field }) => (
                  <ListCategories
                    label="Kho"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue ? { ...newValue, value: newValue._id } : null);
                      setValue("childWarehouse", null);
                    }}
                    warehouse={warehouseCategories?.data ?? []}
                    setSelectedWarehouse={setSelectedWarehouse}
                    hasMore={warehouseCategories?.data?.length >= 20}
                  />
                )}
              />
            </Box>
          )}
          <Divider sx={{ borderColor: "var(--grey) !important", my: "12px" }} />
          {/* BRAND LIST */}
          {productsField.fields.map((field, i) => {
            return (
              <ProductForm
                key={field.id}
                index={i}
                onRemoveProduct={handleRemoveProduct}
                inventoryOptions={inventoryOptions}
                inventoryData={inventory?.data ?? []}
                customerBrand={customerBrandList}
                onProductSearchChange={setProductSearchInput}
                inventoryLoading={inventoryLoading}
              />
            );
          })}
          <Button
            startIcon={<Add />}
            variant="outlined"
            onClick={handleAddProduct}
            sx={{ alignSelf: "flex-end", mt: "12px" }}
          >
            Thêm sản phẩm
          </Button>
          {!!totalCOD && (
            <Box
              sx={{
                borderTop: "1px solid var(--grey)",
                gap: "12px",
                mt: "12px",
                p: "12px",
                width: "100%",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Box textAlign="right">
                <Typography sx={{ fontSize: "16px" }}>Tổng giá trị:</Typography>
                <Typography sx={{ fontSize: "12px" }}>Chưa bao gồm phí ship</Typography>
              </Box>

              <Box gap="12px">
                <Typography fontWeight="bold">{!!totalCOD ? convertIntMoney(totalCOD) : 0} đồng</Typography>
                <Typography
                  fontWeight="bold"
                  variant="caption"
                  fontStyle="italic"
                >
                  ({!!totalCOD ? convertCurrency(Math.ceil(totalCOD)) : 0} đồng)
                </Typography>
              </Box>
            </Box>
          )}
          {/*-------- ADD DELIVERY */}
          <DeliveryInfo />
          <FormControlInput
            name="note"
            label="Ghi chú"
            placeholder="Ghi chú..."
            multiline
            rows={4}
            sx={{ mt: "12px" }}
          />
          <Button
            disabled={isQtyLargerThanStock}
            fullWidth
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            mb="120px"
            sx={{ mt: "12px" }}
          >
            Xác nhận
          </Button>
        </Stack>
      ) : (
        <TransitionGroup>
          <ChangeAddressDialog
            handleChangeAddress={handleChangeAddress}
            customer={customerWatch}
          />
        </TransitionGroup>
      )}
    </FormProvider>
  );
}
